import { useDataContext } from "@/context/DataContext";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePublicDoctors } from "@/hooks/useCMSDoctors";
import { createBooking } from "@/api/bookings";
import i18n from "@/i18n";
import TurnstileWidget from "@/components/base/TurnstileWidget";
import LegalModal from "@/components/feature/LegalModal";
import { getLocalized } from "@/utils/localization";

type SubmitStatus = "idle" | "loading" | "error";

interface BookingSummaryItem {
  icon: string;
  label: string;
  value: string;
}

interface StepPatientInfoProps {
  serviceId: string;
  doctorId: string;
  visitType: string;
  selectedDate: string;
  selectedTime: string;
  packageId?: string;
  onBack: () => void;
  onSuccess: (data: {
    bookingRef: string;
    fullName: string;
    phone: string;
    email?: string;
  }) => void;
}

const VISIT_KEYS: Record<string, string> = {
  first: "booking.step2.first_visit",
  followup: "booking.step2.followup",
  consultation: "booking.step2.consultation",
};

export default function StepPatientInfo({
  serviceId,
  doctorId,
  visitType,
  selectedDate,
  selectedTime,
  packageId,
  onBack,
  onSuccess,
}: StepPatientInfoProps) {
  const { t } = useTranslation();
  const { services: servicesData, packages: allPackages } = useDataContext();

  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");
  const [charCount, setCharCount] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [agreed, setAgreed] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalInitialTab, setLegalInitialTab] = useState<"privacy" | "terms">("privacy");
  const formRef = useRef<HTMLFormElement>(null);
  const { doctors: doctorsDetailed } = usePublicDoctors();

  const isAr = !i18n.language?.startsWith("en");
  const sep = isAr ? " ، " : ", ";
  const doctorIds = doctorId ? doctorId.split(",") : [];
  const selectedDoctors = doctorsDetailed.filter((d) => doctorIds.includes(d.id) || doctorIds.includes(d.doctor_id));
  const doctorNames = selectedDoctors.map(d => getLocalized(i18n.language, d.name, (d as any).nameEn || (d as any).name_en)).join(sep);
  const totalDoctorFees = selectedDoctors.reduce((acc, d) => acc + (parseFloat(String(d.consultationFee || 0)) || 0), 0);

  const selectedServiceIds = serviceId ? serviceId.split(",") : [];
  const selectedServices = servicesData.filter((s) => selectedServiceIds.includes(s.id));
  const serviceNames = selectedServices.map(s => getLocalized(i18n.language, s.name, (s as any).nameEn || (s as any).name_en).split("—")[0].trim()).join(sep);

  const getServicePrice = (s: any) => {
    const rawPrice = s.prices?.[0]?.price ?? s.price;
    const num = parseFloat(String(rawPrice || 0));
    return isNaN(num) ? 0 : num;
  };
  const totalServicePrice = selectedServices.reduce((acc, s) => acc + getServicePrice(s), 0);

  const pkg     = packageId ? allPackages.find((p) => p.id === packageId) : null;

  const accentMap: Record<string, { bg: string; text: string; border: string; iconBg: string; badge: string }> = {
    teal:   { bg: "bg-brand-cream-100", text: "text-brand-forest-700", border: "border-brand-cream-300", iconBg: "bg-brand-cream-200", badge: "bg-brand-forest-600" },
    rose:   { bg: "bg-rose-50",   text: "text-rose-700",   border: "border-rose-200",   iconBg: "bg-rose-100",   badge: "bg-rose-600" },
    orange: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", iconBg: "bg-orange-100", badge: "bg-orange-500" },
    violet: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", iconBg: "bg-violet-100", badge: "bg-violet-600" },
    cyan:   { bg: "bg-cyan-50",   text: "text-cyan-700",   border: "border-cyan-200",   iconBg: "bg-cyan-100",   badge: "bg-cyan-600" },
    amber:  { bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200",  iconBg: "bg-amber-100",  badge: "bg-amber-500" },
    pink:   { bg: "bg-pink-50",   text: "text-pink-700",   border: "border-pink-200",   iconBg: "bg-pink-100",   badge: "bg-pink-600" },
    green:  { bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200",  iconBg: "bg-green-100",  badge: "bg-green-600" },
  };
  const pkgAccent = pkg ? (accentMap[pkg.accentColor] ?? accentMap.teal) : accentMap.teal;

  const MONTH_NAMES = [
    t("months.jan"), t("months.feb"), t("months.mar"), t("months.apr"),
    t("months.may"), t("months.jun"), t("months.jul"), t("months.aug"),
    t("months.sep"), t("months.oct"), t("months.nov"), t("months.dec")
  ];

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const monthIndex = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return `${day} ${MONTH_NAMES[monthIndex]} ${year}`;
    }
    return dateStr;
  };

  const summaryItems: BookingSummaryItem[] = [
    { icon: "ri-stethoscope-line",   label: t("booking.progress.service"),    value: serviceNames || t("booking.step4.undefined") },
    { icon: "ri-user-heart-line",    label: t("booking.progress.doctor"),    value: doctorNames || t("booking.step4.undefined") },
    { icon: "ri-calendar-line",      label: t("booking.confirm.date"),   value: formatDateDisplay(selectedDate) },
    { icon: "ri-time-line",          label: t("booking.confirm.time"),     value: selectedTime },
    { icon: "ri-refresh-line",       label: t("booking.confirm.visit_type"), value: t(VISIT_KEYS[visitType] ?? visitType) },
  ];

  if (totalServicePrice > 0) {
    summaryItems.push({
      icon: "ri-money-dollar-circle-line",
      label: t("booking.confirm.service_price") || "سعر الخدمة",
      value: `${totalServicePrice.toLocaleString()} ${t("label.sar") || "ريال"}`
    });
  }
  if (totalDoctorFees > 0) {
    summaryItems.push({
      icon: "ri-money-dollar-circle-line",
      label: t("booking.confirm.consultation_fees") || "رسوم الاستشارة",
      value: `${totalDoctorFees.toLocaleString()} ${t("label.sar") || "ريال"}`
    });
  } else if (totalServicePrice === 0 && !pkg) {
    summaryItems.push({
      icon: "ri-money-dollar-circle-line",
      label: t("booking.confirm.consultation_fees") || "رسوم الاستشارة",
      value: `0 ${t("label.sar") || "ريال"}`
    });
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus("loading");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((val, key) => data.append(key, val.toString()));
    data.append("service",   selectedServices.map(s => s.name).join(" ، "));
    data.append("doctor",    doctorNames);
    data.append("specialty", selectedDoctors.map(d => d.specialty).join(" ، "));
    data.append("date",      selectedDate);
    data.append("time",      selectedTime);
    data.append("visitType", t(VISIT_KEYS[visitType] ?? visitType));
    if (pkg) data.append("package", pkg.name);

    try {
      const bookingPayload = {
        full_name: formData.get("fullName") as string,
        phone: formData.get("phone") as string,
        email: (formData.get("email") as string) || undefined,
        age: formData.get("age") ? parseInt(formData.get("age") as string) : undefined,
        gender: (formData.get("gender") as string) || undefined,
        complaint: (formData.get("complaint") as string) || undefined,
        service_id: serviceId,
        doctor_id: doctorId,
        package_id: pkg?.id,
        visit_type: visitType,
        selected_date: selectedDate,
        selected_time: selectedTime,
        turnstileToken: turnstileToken || undefined,
      };

      const res = await createBooking(bookingPayload);

      onSuccess({
        bookingRef: res.booking_ref,
        fullName: bookingPayload.full_name,
        phone: bookingPayload.phone,
        email: bookingPayload.email,
      });
    } catch (err) {
      console.error(err);
      setSubmitStatus("error");
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-black text-gray-900 mb-1">{t("booking.step4.heading")}</h2>
        <p className="text-gray-400 text-sm">{t("booking.step4.subheading")}</p>
      </div>

      {/* Package mini-card (if package selected) */}
      {pkg && (
        <div className={`rounded-xl border ${pkgAccent.border} ${pkgAccent.bg} p-4 mb-4 flex items-center gap-3`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${pkgAccent.iconBg}`}>
            <i className={`${pkg.icon} text-lg ${pkgAccent.text}`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black text-gray-900 truncate">{getLocalized(i18n.language, pkg.name, pkg.nameEn)}</p>
            <p className={`text-xs font-semibold ${pkgAccent.text}`}>{getLocalized(i18n.language, pkg.category, pkg.categoryEn)}</p>
          </div>
          <div className="text-end flex-shrink-0">
            {pkg.originalPrice && (
              <p className="text-[10px] text-gray-400 line-through">{pkg.originalPrice.toLocaleString()} {t("label.sar") || "ر"}</p>
            )}
            {pkg.price ? (
              <p className={`text-sm font-black ${pkgAccent.text}`}>{pkg.price.toLocaleString()} {t("label.sar") || "ريال"}</p>
            ) : null}
          </div>
        </div>
      )}

      {/* Booking Summary */}
      <div className="bg-gradient-to-br from-brand-forest-800 to-brand-forest-900 rounded-2xl p-5 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 left-0 h-px bg-gradient-to-r from-transparent via-brand-cream-300/40 to-transparent"></div>
        <p className="text-xs font-bold text-brand-cream-300/70 uppercase tracking-widest mb-4">{t("booking.step4.summary")}</p>
        <div className="grid grid-cols-2 gap-3">
          {summaryItems.map((item, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center bg-brand-cream-300/15 rounded-lg flex-shrink-0 mt-0.5">
                <i className={`${item.icon} text-brand-cream-200 text-xs`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-brand-cream-300/60">{item.label}</p>
                <p className="text-xs font-bold text-white leading-snug truncate">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={onBack}
          className="mt-4 text-xs text-brand-cream-300/70 hover:text-brand-cream-200 font-semibold transition-colors cursor-pointer flex items-center gap-1"
        >
          <i className="ri-edit-line text-xs"></i>
          {t("booking.step4.edit_slot")}
        </button>
      </div>

      {/* Form */}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        data-readdy-form
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {t("booking.step4.name")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              required
              placeholder={t("booking.step4.name_placeholder")}
              className="w-full border border-brand-cream-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-brand-forest-400 focus:ring-2 focus:ring-brand-cream-200 transition-all placeholder-gray-300"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {t("booking.step4.phone")} <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              required
              placeholder="05xxxxxxxx"
              className="w-full border border-brand-cream-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-brand-forest-400 focus:ring-2 focus:ring-brand-cream-200 transition-all placeholder-gray-300"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("booking.step4.email")}</label>
          <input
            type="email"
            name="email"
            placeholder="example@email.com"
            className="w-full border border-brand-cream-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-brand-forest-400 focus:ring-2 focus:ring-brand-cream-200 transition-all placeholder-gray-300"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("booking.step4.age")}</label>
            <input
              type="number"
              name="age"
              min="1"
              max="120"
              placeholder={t("booking.step4.age_placeholder")}
              className="w-full border border-brand-cream-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-brand-forest-400 focus:ring-2 focus:ring-brand-cream-200 transition-all placeholder-gray-300"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("booking.step4.gender")}</label>
            <select
              name="gender"
              className="w-full border border-brand-cream-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-brand-forest-400 focus:ring-2 focus:ring-brand-cream-200 transition-all bg-white cursor-pointer"
            >
              <option value="">{t("booking.step4.select_placeholder")}</option>
              <option value="male">{t("booking.step4.male")}</option>
              <option value="female">{t("booking.step4.female")}</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {t("booking.step4.notes")}
            <span className="text-gray-400 font-normal mr-1 text-xs">{t("booking.step4.optional")}</span>
          </label>
          <textarea
            name="complaint"
            rows={3}
            maxLength={500}
            placeholder={t("booking.step4.notes_placeholder")}
            onChange={(e) => setCharCount(e.target.value.length)}
            className="w-full border border-brand-cream-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-brand-forest-400 focus:ring-2 focus:ring-brand-cream-200 transition-all resize-none placeholder-gray-300"
          />
          <div className="flex justify-between mt-1">
            <p className="text-xs text-gray-400">{t("booking.step4.max_chars")}</p>
            <p className={`text-xs ${charCount > 450 ? "text-red-400" : "text-gray-400"}`}>{charCount}/500</p>
          </div>
        </div>

        <div className="bg-brand-cream-50/80 border border-brand-cream-200 rounded-2xl p-4 transition-all hover:border-brand-forest-300">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="agree"
              required
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 accent-brand-forest-600 rounded cursor-pointer"
            />
            <div className="flex-1 text-xs md:text-sm text-gray-600 leading-relaxed">
              <label htmlFor="agree" className="cursor-pointer">
                {t("booking.step4.agree_prefix", isAr ? "أوافق على " : "I agree to the ")}
              </label>
              <button
                type="button"
                onClick={() => {
                  setLegalInitialTab("privacy");
                  setLegalModalOpen(true);
                }}
                className="text-brand-forest-700 font-bold hover:underline mx-1 cursor-pointer inline-flex items-center gap-0.5"
              >
                <span>{t("booking.step4.privacy_policy", isAr ? "سياسة الخصوصية" : "Privacy Policy")}</span>
                <i className="ri-information-line text-[11px] text-brand-forest-500"></i>
              </button>
              <span>{t("booking.step4.agree_and", isAr ? " و " : " and ")}</span>
              <button
                type="button"
                onClick={() => {
                  setLegalInitialTab("terms");
                  setLegalModalOpen(true);
                }}
                className="text-brand-forest-700 font-bold hover:underline mx-1 cursor-pointer inline-flex items-center gap-0.5"
              >
                <span>{t("booking.step4.terms_of_use", isAr ? "شروط الاستخدام" : "Terms of Use")}</span>
                <i className="ri-information-line text-[11px] text-brand-forest-500"></i>
              </button>
              <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1.5">
                <i className="ri-shield-check-line text-emerald-600 text-xs"></i>
                <span>
                  {isAr 
                    ? "بياناتك مشفرة ومحمية بالكامل وفق معايير وزارة الصحة والأنظمة المعتمدة" 
                    : "Your medical data is encrypted and strictly protected under approved healthcare regulations"}
                </span>
              </p>
            </div>
          </div>
        </div>

        <LegalModal
          isOpen={legalModalOpen}
          initialTab={legalInitialTab}
          onClose={() => setLegalModalOpen(false)}
          onAccept={() => setAgreed(true)}
        />

        {submitStatus === "error" && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-xl">
            <i className="ri-error-warning-line"></i>
            {t("booking.step4.submit_error")}
          </div>
        )}

        <TurnstileWidget 
          onVerify={(token) => setTurnstileToken(token)} 
          action="booking" 
        />

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 border-2 border-gray-200 text-gray-600 font-semibold px-5 py-3.5 rounded-full hover:border-gray-300 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className={i18n.language === "ar" ? "ri-arrow-right-line" : "ri-arrow-left-line"}></i>
            {t("btn.back")}
          </button>
          <button
            type="submit"
            disabled={submitStatus === "loading"}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-forest-600 hover:bg-brand-forest-700 text-white font-bold py-3.5 rounded-full transition-all whitespace-nowrap cursor-pointer disabled:opacity-70"
          >
            {submitStatus === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                {t("booking.step4.confirming")}
              </>
            ) : (
              <>
                <i className="ri-calendar-check-line"></i>
                {t("booking.step4.confirm")}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
